import React, { useMemo, useRef, useState } from 'react';

import { getVideoCover, isVideo } from '@libs/NwUtils';

import { NwButton } from '@components/Gui/NwButton';
import NwSegmented from '@components/Gui/NwForm/NwSegmented';
import { usePlaylistEditor, usePlaylistEditorDispatch } from '@components/PlaylistEditor/PlaylistEditorContext';

import { MobileMediaPickerWrapper } from './MobileBookEditorUI';
import MobilePickerThumb from './MobilePickerThumb';

const MEDIA_TYPE_OPTIONS = [
    { label: 'Photos', value: 'Photo' },
    { label: 'Videos', value: 'Video' },
];

/**
 * Selezione dei media da aggiungere al book. I badge numerano l'ordine dei tap:
 * e' l'ordine con cui il blocco entra in coda al book, con una sola impaginazione.
 */
const MobileMediaPicker = ({ onClose, onPreviewImage }) => {
    const { documentsList, hideUsedPictures, isFetchingDocuments, mediaTypeToDisplay, playlistItems, singlePage } =
        usePlaylistEditor();
    const dispatch = usePlaylistEditorDispatch();

    const [picks, setPicks] = useState([]);
    const blankCounter = useRef(0);

    const isInBook = (document) => playlistItems.some((item) => item.ID === document.ID);

    const availableDocuments = useMemo(() => {
        const readyDocuments = documentsList.filter((document) => document.Standard && document.Standard.Url);
        if (hideUsedPictures) {
            return readyDocuments.filter((document) => !playlistItems.some((item) => item.ID === document.ID));
        }
        return readyDocuments;
    }, [documentsList, hideUsedPictures, playlistItems]);

    const orderOf = (document) => {
        const position = picks.findIndex((pick) => pick.key === document.ID);
        return position === -1 ? null : position + 1;
    };

    const handleToggle = (document) => {
        setPicks((current) => {
            const position = current.findIndex((pick) => pick.key === document.ID);
            if (position !== -1) {
                return current.filter((pick, index) => index !== position);
            }
            return [...current, { key: document.ID, media: document }];
        });
    };

    const handleAddBlankPage = () => {
        blankCounter.current += 1;
        setPicks((current) => [...current, { key: `blank-${blankCounter.current}`, placeholder: true }]);
    };

    const handleRemovePick = (key) => {
        setPicks((current) => current.filter((pick) => pick.key !== key));
    };

    const handlePreview = (document) => {
        if (isVideo(document)) {
            dispatch({ type: 'SetPreviewVideo', value: document });
        } else {
            onPreviewImage(document.HD ? document.HD.Url : document.Standard.Url);
        }
    };

    const handleMediaTypeChange = (value) => {
        dispatch({ type: 'ChangeMediaTypeToDisplay', value });
    };

    const handleToggleHideUsed = () => {
        dispatch({ type: 'ToggleHideUsedPictures', value: !hideUsedPictures });
    };

    const handleCommit = () => {
        dispatch({
            type: 'AddPlaylistItems',
            value: picks.map((pick) => (pick.placeholder ? { placeholder: true } : { media: pick.media })),
        });
        onClose();
    };

    return (
        <MobileMediaPickerWrapper>
            <div className="picker-header">
                <div className="picker-header-row">
                    <button type="button" className="picker-link" onClick={onClose}>
                        Cancel
                    </button>
                    <h2>Add media</h2>
                    <button
                        type="button"
                        className="picker-link accent"
                        disabled={picks.length === 0}
                        onClick={() => setPicks([])}
                    >
                        Clear
                    </button>
                </div>
                <div className="picker-filters">
                    <NwSegmented
                        size="small"
                        options={MEDIA_TYPE_OPTIONS}
                        value={mediaTypeToDisplay}
                        onChange={handleMediaTypeChange}
                    />
                    <button
                        type="button"
                        className="picker-used-toggle"
                        aria-pressed={hideUsedPictures}
                        onClick={handleToggleHideUsed}
                    >
                        Hide used
                    </button>
                </div>
            </div>

            {isFetchingDocuments ? (
                <p className="picker-message">Loading the archive…</p>
            ) : (
                <div className="picker-grid">
                    {mediaTypeToDisplay === 'Photo' && !singlePage && (
                        <div className="picker-thumb picker-blank">
                            <span className="blank-mark">Blank page</span>
                            <button
                                type="button"
                                className="thumb-select"
                                aria-label="Add a blank page"
                                onClick={handleAddBlankPage}
                            />
                        </div>
                    )}
                    {availableDocuments.map((document) => (
                        <MobilePickerThumb
                            key={document.ID}
                            document={document}
                            isInBook={isInBook(document)}
                            orderNumber={orderOf(document)}
                            onToggle={handleToggle}
                            onPreview={handlePreview}
                        />
                    ))}
                    {availableDocuments.length === 0 && (
                        <p className="picker-message">
                            {hideUsedPictures
                                ? 'Every item of this type is already in the book.'
                                : 'This model has no media of this type yet.'}
                        </p>
                    )}
                </div>
            )}

            <div className="picker-tray">
                {picks.length === 0 ? (
                    <p className="tray-hint">Tap to select — badges show the order</p>
                ) : (
                    <div className="tray-strip">
                        {picks.map((pick) => (
                            <button
                                type="button"
                                key={pick.key}
                                className="tray-chip"
                                aria-label="Remove from selection"
                                onClick={() => handleRemovePick(pick.key)}
                            >
                                {!pick.placeholder && (
                                    <img
                                        src={
                                            isVideo(pick.media)
                                                ? getVideoCover(pick.media)
                                                : pick.media.Thumbnail && pick.media.Thumbnail.Url
                                        }
                                        alt=""
                                    />
                                )}
                                <span className="chip-remove">✕</span>
                            </button>
                        ))}
                    </div>
                )}
                <NwButton
                    primary
                    disabled={picks.length === 0}
                    onClick={handleCommit}
                    label={picks.length ? `Add ${picks.length} to book` : 'Add to book'}
                />
            </div>
        </MobileMediaPickerWrapper>
    );
};

export default MobileMediaPicker;
